import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../../../Store/Store';
import { useNavigationTracking } from '../../../hooks/useNavigationTracking';

interface CustomerIdScreenProps {
  onNext: () => void;
  autoSkipSeconds?: number;
  showSkipNextScreenButton?: boolean;
  showBackButton?: boolean;
  onBack?: () => void;
  navigateToProduct?: (
    productCode: string,
    preserveCustomerId?: boolean
  ) => void;
  feedbackSessionId?: string;
}

export default function CustomerIdScreen({
  onNext,
  autoSkipSeconds = 0,
  showSkipNextScreenButton = false,
  showBackButton = false,
  onBack,
  navigateToProduct,
  feedbackSessionId,
}: CustomerIdScreenProps) {
  const {
    product,
    style,
    cycleCustomerId,
    refreshCycleCustomerId,
    isEventMode,
  } = useAppContext();
  const navigate = useNavigate();
  const { trackNavigation } = useNavigationTracking();
  const [customerId, setCustomerId] = useState<string>(cycleCustomerId || '');
  const autoSkipTimerRef = useRef<NodeJS.Timeout | null>(null);
  const onNextRef = useRef(onNext);
  const isProductMode = true;

  useEffect(() => {
    onNextRef.current = onNext;
  }, [onNext]);

  // Sync whenever the context value updates (set by refreshCycleCustomerId)
  useEffect(() => {
    if (cycleCustomerId) setCustomerId(cycleCustomerId);
  }, [cycleCustomerId]);

  useEffect(() => {
    if (autoSkipTimerRef.current) {
      clearTimeout(autoSkipTimerRef.current);
      autoSkipTimerRef.current = null;
    }
    if (autoSkipSeconds > 0) {
      autoSkipTimerRef.current = setTimeout(() => {
        onNextRef.current();
      }, autoSkipSeconds * 1000);
    }
    return () => {
      if (autoSkipTimerRef.current) {
        clearTimeout(autoSkipTimerRef.current);
        autoSkipTimerRef.current = null;
      }
    };
  }, [autoSkipSeconds]);

  const copyToClipboard = async () => {
    if (!customerId) return;
    try {
      await navigator.clipboard.writeText(customerId);
      return;
    } catch (_e) {}
    const textarea = document.createElement('textarea');
    textarea.value = customerId;
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    textarea.style.top = '-9999px';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    try {
      document.execCommand('copy');
    } catch (_e) {}
    document.body.removeChild(textarea);
  };

  const handleSkipNextScreen = () => {
    if (autoSkipTimerRef.current) {
      clearTimeout(autoSkipTimerRef.current);
      autoSkipTimerRef.current = null;
    }
    onNext();
  };

  const handleSkipToProduct = () => {
    if (!product?.redirect_product) return;
    if (autoSkipTimerRef.current) {
      clearTimeout(autoSkipTimerRef.current);
      autoSkipTimerRef.current = null;
    }
    trackNavigation({
      from_screen: 'customer_id',
      to_screen: 'skip_to_product',
      navigation_type: 'skip',
      screen_index: 0,
      total_screens: 1,
    });
    if (navigateToProduct) {
      navigateToProduct(
        product.redirect_product,
        isEventMode || Boolean(product.redirect_with_same_customer_id)
      );
    } else {
      navigate(`/?product=${product.redirect_product}`);
    }
  };

  const handleRestart = () => {
    if (!product?.redirect_product) return;
    if (autoSkipTimerRef.current) {
      clearTimeout(autoSkipTimerRef.current);
      autoSkipTimerRef.current = null;
    }
    refreshCycleCustomerId();
    if (navigateToProduct) {
      navigateToProduct(product.redirect_product);
    } else {
      navigate(`/?product=${product.redirect_product}`);
    }
  };

  const copyBtnBg =
    style?.customer_id_copy_button_bg_color ||
    style?.text_review_button_bg_color ||
    'linear-gradient(to right, #6991F8 0%, #07296a 100%)';
  const copyBtnTextColor =
    style?.customer_id_copy_button_text_color ||
    style?.text_review_button_text_color ||
    'white';
  const copyBtnShadow =
    style?.customer_id_copy_button_shadow_color ||
    style?.text_review_button_shadow_color ||
    '#FFFFFF';
  const copyBtnIndication = style?.customer_id_copy_button_indication || 'Copy';

  const restartIndication =
    style?.customer_id_restart_button_indication || 'Restart';
  const restartTextColor =
    style?.customer_id_restart_button_text_color || '#07296A';
  const showRestartButton = Boolean(product?.redirect_product);

  const showSkipProduct = Boolean(
    product?.show_skip_product_button && product?.redirect_product
  );
  const showAnySkip = showSkipProduct;

  return (
    <div style={{ margin: '0 auto', position: 'relative', width: '100%' }}>
      <div
        className="review-actions"
        style={{
          position: 'absolute',
          top: '19%',
          height: isProductMode ? '100%' : '68%',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          className="review-block"
          style={{
            height: isProductMode ? '100%' : '75.5%',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            marginTop: 'clamp(20px, 4vh, 56px)',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <div
              style={{
                fontWeight: 'bold',
                color: '#07296A',
                fontSize: 'clamp(2rem, 8vh, 5rem)',
                letterSpacing: '0.08em',
                marginTop: 'clamp(12px, 3vh, 32px)',
                lineHeight: 1,
                textAlign: 'center',
                wordBreak: 'break-all',
              }}
            >
              {customerId || '____'}
            </div>
            <div
              style={{
                marginTop: '2vh',
                fontWeight: 'bold',
                fontSize: '100%',
                color: '#07296A',
                minHeight: '1.2em',
              }}
              aria-hidden
            />
          </div>
        </div>
        <div
          className="button-block"
          style={{
            height: isProductMode ? '6vh' : '24.5%',
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginTop: isProductMode ? 'clamp(24px, 6vh, 80px)' : '16px',
          }}
        >
          <button
            type="button"
            onClick={copyToClipboard}
            style={{
              height: '4.6vh',
              minHeight: '36px',
              width: 'min(34vh, 90vw)',
              maxWidth: '420px',
              borderRadius: '7px',
              padding: '0 20px',
              fontSize: '90%',
              fontWeight: 'bold',
              color: copyBtnTextColor,
              background: copyBtnBg,
              border: 'none',
              cursor: customerId ? 'pointer' : 'not-allowed',
              boxShadow: '0 5px 5px ' + copyBtnShadow,
            }}
            disabled={!customerId}
          >
            <span dangerouslySetInnerHTML={{ __html: copyBtnIndication }} />
          </button>
        </div>
        {showRestartButton && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
              marginTop: 'clamp(16px, 3vh, 40px)',
            }}
          >
            <button
              type="button"
              onClick={handleRestart}
              style={{
                fontSize: '18px',
                textDecoration: 'underline',
                color: restartTextColor,
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              <span dangerouslySetInnerHTML={{ __html: restartIndication! }} />
            </button>
          </div>
        )}
        {showBackButton && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
              marginTop: 'clamp(16px, 3vh, 40px)',
            }}
          >
            <button
              type="button"
              onClick={() => onBack?.()}
              style={{
                fontSize: '18px',
                textDecoration: 'underline',
                color: style?.back_button_text_color || '#07296A',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              <span
                dangerouslySetInnerHTML={{
                  __html: style?.back_button_indication || 'Back',
                }}
              />
            </button>
          </div>
        )}
        {showAnySkip && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
              marginTop: 'clamp(16px, 3vh, 40px)',
            }}
          >
            {showSkipProduct && (
              <button
                type="button"
                onClick={handleSkipToProduct}
                style={{
                  fontSize: '18px',
                  textDecoration: 'underline',
                  color: style?.skip_product_button_text_color || '#07296A',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  marginTop: 0,
                }}
              >
                <span
                  dangerouslySetInnerHTML={{
                    __html:
                      style?.skip_product_button_indication ||
                      'Skip to product',
                  }}
                />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
